// ==============================
// Imports
// ==============================

// MUI Components
import { useState } from 'react';
import {
  Delete,
  Edit,
  FileDownload,
  Search,
  Visibility,
} from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Avatar,
  Stack,
  IconButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  // TableSortLabel,
} from '@mui/material';
import { TableNoData, TableSpacerRow } from './common/TableEmptyState';
import TableLoader from './common/TableLoader';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

// Local libraries

// Local Components
// import { notifyActionSuccess } from '../utils/toast';
import GenderChip from './common/GenderChip';

// Hook
import { useUserController } from '../hooks/useUserTable';
import { useIsMobile } from '../hooks/useResponsive';
import UserCardList from './UserCardList';
import { User } from '../types/UserTable.type';
import UserInfoDialog from './FormDialog/UserInfoDialog';
import {
  rowHoverBg,
  selectedRowBg,
  selectedRowBgOpaque,
} from '../utils/rowHighlight';
import { useTranslation } from '../i18n';

const UserTable = () => {
  const { t } = useTranslation();
  const {
    users,
    page,
    limit,
    totalItems,
    searchTerm,
    userTypeFilter,
    duplicatePhoneFilter,
    duplicateEmailFilter,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterChange,
    handleToggleDuplicatePhone,
    handleToggleDuplicateEmail,
    clearFilters,
    fetchUsers,
    handleDownloadUsers,
    downloadLoading,
    loading,
    setPage,
    openUserInfoDialog,
    setOpenUserInfoDialog,
    selectedUser,
    setSelectedUser,
    handleCloseUserInfoDialog,
    handleDeleteUser,
    openDeleteUserDialog,
    setOpenDeleteUserDialog,
    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
  } = useUserController();

  const isMobile = useIsMobile();
  // Reuse the info dialog as a read-only "view profile" when the eye is clicked.
  const [userInfoViewOnly, setUserInfoViewOnly] = useState(false);

  // Row actions, shared by the desktop table and the mobile card list.
  const handleEditUser = (user: User) => {
    localStorage.setItem('selectedUserType', user.userType);
    setSelectedUser(user);
    setUserInfoViewOnly(false);
    setOpenUserInfoDialog(true);
  };
  const handleRequestDeleteUser = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteUserDialog(true);
  };

  // ==============================
  // Render
  // ==============================
  return (
    <Paper>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          size="medium"
          placeholder={t('user.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchUsers();
              setPage(1);
              // notifyActionSuccess('Search');
            }
          }}
          sx={{ width: { xs: '100%', sm: 360, md: 500 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              size: 'small',
            },
          }}
        />

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {/* Duplicate-account filters — find accounts sharing a phone/email so
              they can be merged. Businesses may legitimately reuse contact
              details, so these are off by default. */}
          <FormGroup row sx={{ mr: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={duplicatePhoneFilter}
                  onChange={handleToggleDuplicatePhone}
                />
              }
              label={t('user.samePhone')}
              slotProps={{ typography: { variant: 'body2' } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={duplicateEmailFilter}
                  onChange={handleToggleDuplicateEmail}
                />
              }
              label={t('user.sameEmail')}
              slotProps={{ typography: { variant: 'body2' } }}
            />
          </FormGroup>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={userTypeFilter}
              onChange={handleFilterChange}
            >
              <MenuItem value="All">{t('user.userTypes')}</MenuItem>
              <MenuItem value="CHILD">{t('user.child')}</MenuItem>
              <MenuItem value="ADULT">{t('user.adult')}</MenuItem>
              <MenuItem value="BUSINESS">{t('user.business')}</MenuItem>
            </Select>
          </FormControl>

          {/* Clear filters button */}
          {userTypeFilter !== 'All' ||
          searchTerm ||
          duplicatePhoneFilter ||
          duplicateEmailFilter ? (
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={clearFilters}
              startIcon={<FilterAltOffIcon />}
            >
              {t('user.clear')}
            </Button>
          ) : null}

          <Tooltip title={t('user.downloadList')}>
            <IconButton
              onClick={handleDownloadUsers}
              loading={downloadLoading}
              disabled={totalItems === 0 || downloadLoading}
            >
              <FileDownload />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {isMobile ? (
        <UserCardList
          users={users}
          onEdit={handleEditUser}
          onDelete={handleRequestDeleteUser}
        />
      ) : (
        <TableContainer>
          <Table size="small" padding="checkbox">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#4da7db' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colId')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colAvatar')}
                  </Typography>
                </TableCell>
                {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  Full Name (Preferred Name)
                </Typography>
              </TableCell> */}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colFirstName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colLastName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colPreferredName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colBusinessName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colDateOfBirth')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colGender')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colEmail')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colPhone')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colParent')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('user.colUserType')}
                  </Typography>
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    right: -16,
                    zIndex: '1 !important',
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    color="#fff"
                    bgcolor={'#4da7db'}
                    borderLeft={1}
                    textAlign="center"
                    className="!px-2"
                  >
                    {t('user.colActions')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            {/* ********** */}
            {/* DATA MANIPULATION */}
            {/* ********** */}
            <TableBody>
              {loading ? (
                <TableLoader colSpan={13} />
              ) : (
                <>
                  {users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${user.id}`
                        ? selectedRowBg
                        : 'inherit',
                  }}
                  onClick={() => {
                    if (highlightedRow === `data_${user.id}`) {
                      setHighlightedRow(null);
                    } else {
                      setHighlightedRow(`data_${user.id}`);
                    }
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Avatar
                      src={user.pictureUrl}
                      sx={{
                        margin: 0.4,
                      }}
                    />
                  </TableCell>
                  {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>{`${user.firstName} ${user.lastName} (${user.preferredName || 'None'})`}</TableCell> */}
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                  >{`${user.firstName}`}</TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                  >{`${user.lastName}`}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const preferredName = user.preferredName;
                      // Treat empty AND the literal "None" string as no value.
                      const isEmpty =
                        !preferredName ||
                        preferredName.trim().toLowerCase() === 'none';
                      return isEmpty ? (
                        <span style={{ opacity: 0.4 }}>None</span>
                      ) : (
                        preferredName
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const businessName = user.businessName;
                      // Empty business name -> faded "None".
                      const isEmpty =
                        !businessName ||
                        businessName.trim().toLowerCase() === 'none';
                      return isEmpty ? (
                        <span style={{ opacity: 0.4 }}>None</span>
                      ) : (
                        businessName
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {user.userType === 'BUSINESS'
                      ? '---'
                      : new Date(user.dob).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <GenderChip value={user.gender} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const email = user.email;
                      // No email on file — fade the placeholder.
                      if (!email)
                        return <span style={{ opacity: 0.4 }}>None</span>;
                      // Real email — underline it and let admins click through to
                      // compose a message in Gmail.
                      return (
                        <Tooltip title="Click to open gmail">
                          <a
                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: 'inherit',
                            }}
                          >
                            {email}
                          </a>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const phone = user.phone;
                      // Placeholder number used when a real phone wasn't collected.
                      const PLACEHOLDER_PHONE = '0909999999';
                      if (!phone)
                        return <span style={{ opacity: 0.4 }}>None</span>;
                      if (phone === PLACEHOLDER_PHONE) {
                        // Faded: this is a placeholder, not a real number.
                        return <span style={{ opacity: 0.4 }}>{phone}</span>;
                      }
                      // Real number provided by the customer — underline it and
                      // let admins click through to the customer's Zalo.
                      return (
                        <Tooltip title="Click to open zalo">
                          <a
                            href={`https://zalo.me/${phone}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: 'inherit',
                            }}
                          >
                            {phone}
                          </a>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>{user.phone || '(+84) ---'}</div>
                  <div>{user.email || 'user@aquatics.com'}</div>
                </TableCell> */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const parent = user.parent;
                      // Treat empty AND the literal "None" string as no value.
                      const isEmpty =
                        !parent || parent.trim().toLowerCase() === 'none';
                      return isEmpty ? (
                        <span style={{ opacity: 0.4 }}>None</span>
                      ) : (
                        parent
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <div>{user.userType || 'CHILD/ADULT'}</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                      {new Date(user.createdAt).toLocaleString()}
                    </div>
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      right: -16,
                      zIndex: '1 !important',
                      backgroundColor:
                        highlightedRow === `data_${user.id}`
                          ? selectedRowBgOpaque
                          : 'background.paper',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        backgroundColor:
                          highlightedRow === `data_${user.id}`
                            ? selectedRowBgOpaque
                            : 'background.paper',
                        borderLeft: 1,
                        borderColor: 'divider',
                        px: 1,
                      }}
                    >
                      <Tooltip
                        title={t('user.viewUser')}
                        onClick={() => {
                          setSelectedUser(user);
                          setUserInfoViewOnly(true);
                          setOpenUserInfoDialog(true);
                        }}
                      >
                        <IconButton color="primary" aria-label="view user">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={t('user.editUser')}
                        onClick={() => {
                          localStorage.setItem(
                            'selectedUserType',
                            user.userType,
                          );
                          setSelectedUser(user);
                          setUserInfoViewOnly(false);
                          setOpenUserInfoDialog(true);
                        }}
                      >
                        <IconButton color="primary" aria-label="edit user">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={t('user.deleteUser')}
                        onClick={() => {
                          setSelectedUser(user);
                          setOpenDeleteUserDialog(true);
                        }}
                      >
                        <IconButton color="error" aria-label="delete user">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
                  {users.length === 0 ? (
                    <TableNoData />
                  ) : (
                    <TableSpacerRow rows={emptyRows} />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Other data information */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pl={2}
      >
        <Typography variant="body2" color="textSecondary">
          {t('user.lastSync')}:{' '}
          {lastSyncTime?.format('DD/MM/YYYY - HH:mm:ss') || t('user.never')}
        </Typography>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Box>

      {/* Dialog */}
      <UserInfoDialog
        open={openUserInfoDialog}
        handleClose={handleCloseUserInfoDialog}
        user={selectedUser}
        fetchUsers={fetchUsers}
        viewOnly={userInfoViewOnly}
      />

      {/* Confirm Delete */}
      <Dialog
        fullWidth
        open={openDeleteUserDialog}
        onClose={() => setOpenDeleteUserDialog(false)}
      >
        <DialogTitle>{t('user.deleteUserTitle')}</DialogTitle>
        <DialogContent>{t('user.deleteUserConfirm')}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteUserDialog(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (selectedUser) {
                handleDeleteUser(selectedUser);
                setOpenDeleteUserDialog(false);
              }
            }}
          >
            {t('user.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserTable;
